import { useEffect, useState, useCallback } from "react";
import { venturesApi, webhookApi } from "@/lib/api-client";
import { fmtUSDFull, CATEGORY_META } from "@/lib/format";
import { Pencil, Check, X, Plus, Trash2, Radio, Copy, RefreshCw, Sliders } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import VentureDetailsModal from "@/components/uc/VentureDetailsModal";

const PRIORITY_COLOR = { critical: "#EF4444", high: "#F59E0B", medium: "#3B82F6", low: "#71717A" };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VentureCockpit({ onChanged }) {
  const [ventures, setVentures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [editName, setEditName] = useState("");
  const [adding, setAdding] = useState(false);
  const [newVenture, setNewVenture] = useState({ category: "real_estate", name: "", value: "" });
  const [detailsVenture, setDetailsVenture] = useState(null);

  const load = useCallback(async () => {
    try {
      const items = await venturesApi.list();
      setVentures(items);
    } catch (e) {
      console.error("ventures load failed", e);
      toast.error("Failed to load ventures");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (v) => {
    setEditingId(v.id);
    setEditVal(String(v.value));
    setEditName(v.name);
  };

  const saveEdit = async (v) => {
    try {
      await venturesApi.update(v.id, { value: parseFloat(editVal) || 0, name: editName });
      toast.success("Updated");
      setEditingId(null);
      await load();
      onChanged?.();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Remove "${v.name}"?`)) return;
    try {
      await venturesApi.remove(v.id);
      toast.success("Venture removed");
      await load();
      onChanged?.();
    } catch { toast.error("Remove failed"); }
  };

  const addVenture = async () => {
    if (!newVenture.name || !newVenture.value) return toast.error("Name and value required");
    try {
      await venturesApi.create({
        category: newVenture.category,
        name: newVenture.name,
        value: parseFloat(newVenture.value) || 0,
      });
      toast.success("Venture added");
      setAdding(false);
      setNewVenture({ category: "real_estate", name: "", value: "" });
      await load();
      onChanged?.();
    } catch { toast.error("Create failed"); }
  };

  const grouped = ventures.reduce((acc, v) => {
    (acc[v.category] ||= []).push(v);
    return acc;
  }, {});

  return (
    <section className="uc-card p-6 md:p-8 uc-fade" data-testid="venture-cockpit">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="uc-chip mb-2"><span className="uc-dot bg-white/60"></span> 10-VENTURE COCKPIT</div>
          <h2 className="font-serif-display text-2xl">Manual Connectors</h2>
        </div>
        <button
          data-testid="add-venture-btn"
          onClick={() => setAdding(!adding)}
          className="uc-chip hover:bg-white/5 transition-colors uc-focus"
        >
          <Plus size={12} /> New Venture
        </button>
      </div>

      {adding && (
        <div className="p-4 mb-4 border border-amber-500/30 bg-amber-500/5 grid grid-cols-1 md:grid-cols-5 gap-2 items-end" data-testid="add-venture-form">
          <div>
            <label className="font-mono-uc text-[10px] uppercase text-white/50 block mb-1">Category</label>
            <Select value={newVenture.category} onValueChange={(v) => setNewVenture({ ...newVenture, category: v })}>
              <SelectTrigger data-testid="new-venture-category" className="h-10 rounded-none bg-[#121212] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="font-mono-uc text-[10px] uppercase text-white/50 block mb-1">Name</label>
            <Input
              data-testid="new-venture-name"
              value={newVenture.name}
              onChange={(e) => setNewVenture({ ...newVenture, name: e.target.value })}
              placeholder="e.g. Boutique Hotel"
              className="h-10 rounded-none bg-[#121212] border-white/10"
            />
          </div>
          <div>
            <label className="font-mono-uc text-[10px] uppercase text-white/50 block mb-1">Value (USD)</label>
            <Input
              data-testid="new-venture-value"
              type="number"
              value={newVenture.value}
              onChange={(e) => setNewVenture({ ...newVenture, value: e.target.value })}
              placeholder="250000"
              className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc"
            />
          </div>
          <div className="flex gap-2">
            <Button data-testid="new-venture-save" onClick={addVenture} className="uc-btn-amber rounded-none h-10 flex-1">Save</Button>
            <Button data-testid="new-venture-cancel" onClick={() => setAdding(false)} variant="ghost" className="rounded-none h-10 border border-white/10 text-white/70">Cancel</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(CATEGORY_META).map(([cat, meta]) => (
          <div key={cat} className="uc-card-elevated border border-white/5 p-4" data-testid={`cockpit-cat-${cat}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="uc-dot" style={{ background: meta.color }}></span>
                <h3 className="font-serif-display text-lg">{meta.label}</h3>
              </div>
              <span className="font-mono-uc text-[10px] text-white/40 uppercase tracking-wider">
                {(grouped[cat] || []).length} positions
              </span>
            </div>
            <div className="space-y-2">
              {(grouped[cat] || []).map((v) => (
                <div key={v.id} className="group flex items-center justify-between py-2 px-3 border border-white/5 hover:border-white/15 transition-colors" data-testid={`venture-${v.id}`}>
                  {editingId === v.id ? (
                    <div className="flex-1 flex gap-2 items-center">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="h-8 rounded-none bg-black border-white/10 text-sm"
                      />
                      <Input
                        data-testid={`edit-value-${v.id}`}
                        type="number"
                        value={editVal}
                        onChange={(e) => setEditVal(e.target.value)}
                        className="h-8 rounded-none bg-black border-white/10 font-mono-uc w-32 text-sm"
                      />
                      <button data-testid={`save-venture-${v.id}`} onClick={() => saveEdit(v)} className="text-emerald-400 hover:text-emerald-300"><Check size={14} /></button>
                      <button onClick={() => setEditingId(null)} className="text-white/50 hover:text-white"><X size={14} /></button>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-sm text-white/90 truncate">{v.name}</div>
                          {v.fix_priority && v.fix_priority !== "none" && (
                            <span className="font-mono-uc text-[9px] uppercase tracking-wider px-1.5 py-0 border flex items-center gap-1"
                                  style={{ borderColor: PRIORITY_COLOR[v.fix_priority], color: PRIORITY_COLOR[v.fix_priority] }}
                                  data-testid={`fix-badge-${v.id}`}>
                              <Radio size={8} className="animate-pulse"/> {v.fix_priority}
                            </span>
                          )}
                        </div>
                        {v.notes && <div className="text-[11px] text-white/40 truncate">{v.notes}</div>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono-uc text-sm text-white" data-testid={`venture-value-${v.id}`}>
                          {fmtUSDFull(v.value)}
                        </span>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 items-center">
                          <VentureWebhook venture={v} onRegen={load}/>
                          <button data-testid={`details-venture-${v.id}`} onClick={() => setDetailsVenture(v)} className="text-white/40 hover:text-blue-400" title="Data intake"><Sliders size={12} /></button>
                          <button data-testid={`edit-venture-${v.id}`} onClick={() => startEdit(v)} className="text-white/40 hover:text-amber-400"><Pencil size={12} /></button>
                          <button data-testid={`delete-venture-${v.id}`} onClick={() => remove(v)} className="text-white/40 hover:text-red-400"><Trash2 size={12} /></button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {(!grouped[cat] || grouped[cat].length === 0) && (
                <div className="text-white/30 text-xs py-2 italic">No positions in this category.</div>
              )}
            </div>
          </div>
        ))}
      </div>
      {loading && <div className="text-white/40 text-sm py-6">Loading cockpit…</div>}
      <VentureDetailsModal venture={detailsVenture} open={!!detailsVenture} onOpenChange={(v) => !v && setDetailsVenture(null)} onSaved={() => { load(); onChanged?.(); }}/>
    </section>
  );
}

function VentureWebhook({ venture, onRegen }) {
  const [tok, setTok] = useState(venture.webhook_token);
  const url = `${BACKEND_URL}/api/webhooks/venture/${tok}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Webhook URL copied");
    } catch { toast.error("Copy failed"); }
  };
  const regen = async () => {
    if (!window.confirm("Regenerate webhook URL? The old URL will stop working.")) return;
    try {
      const r = await webhookApi.regenerate(venture.id);
      setTok(r.webhook_token);
      toast.success("Webhook regenerated");
      onRegen?.();
    } catch { toast.error("Regen failed"); }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`webhook-btn-${venture.id}`} className="text-white/40 hover:text-blue-400" title="Webhook">
          <Radio size={12}/>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 bg-[#0F0F0F] border-white/10 text-white p-3" side="left">
        <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 mb-2">Realtime Webhook</div>
        <div className="p-2 border border-white/10 bg-black text-[11px] font-mono-uc text-white/80 break-all mb-2" data-testid={`webhook-url-${venture.id}`}>
          {url}
        </div>
        <div className="flex gap-2">
          <button data-testid={`webhook-copy-${venture.id}`} onClick={copy} className="flex-1 uc-chip hover:bg-white/5 justify-center">
            <Copy size={10}/> Copy URL
          </button>
          <button data-testid={`webhook-regen-${venture.id}`} onClick={regen} className="uc-chip hover:bg-white/5 justify-center">
            <RefreshCw size={10}/> Regenerate
          </button>
        </div>
        <div className="mt-3 text-[10px] text-white/40 leading-relaxed">
          POST JSON like:<br/>
          <code className="text-white/70">{'{"event_type":"payment_failed","severity":"high","message":"..."}'}</code>
        </div>
      </PopoverContent>
    </Popover>
  );
}
